<!-- See: https://svelte.dev/docs/svelte-components#script-context-module -->
<script module lang="ts">
  let openModalsCount = 0
</script>

<script lang="ts">
  import { onDestroy, onMount } from 'svelte'
  import { tick } from 'svelte'
  import { autofocus } from '#app/lib/components/actions/autofocus'
  import { icon } from '#app/lib/icons'
  import { getActionKey } from '#app/lib/key_events'
  import { vent } from '#app/radio'
  import type { ClickEvent, KeyDownEvent } from '#app/types/declarations/svelte'
  import { I18n } from '#user/lib/i18n'
  import type { Snippet } from 'svelte'

  const {
    size = 'medium',
    closeOnClick = false,
    children,
    onCloseModal,
  }: {
    size?: string
    closeOnClick?: boolean
    children?: Snippet
    onCloseModal: () => void
  } = $props()

  async function closeModal () {
    onCloseModal()
    await tick()
    if (openModalsCount > 0) giveFocusToPreviousModal()
  }

  function giveFocusToPreviousModal () {
    const remainingModalOverlays = document.querySelectorAll('.modal-overlay')
    const lastModal = remainingModalOverlays[remainingModalOverlays.length - 1]
    // @ts-expect-error
    lastModal.focus()
  }

  function onOverlayKeyUp (e) {
    const key = getActionKey(e)
    if (key === 'esc') closeModal()
    e.stopPropagation()
  }

  onMount(() => {
    if (openModalsCount === 0) vent.trigger('overlay:shown')
    openModalsCount++
  })
  onDestroy(() => {
    openModalsCount--
    if (openModalsCount === 0) vent.trigger('overlay:hidden')
  })
  function onModalClick (e: ClickEvent) {
    if (closeOnClick) closeModal()
    vent.trigger('modal:click')
    // stopPropagation so that only clicks on overlay trigger a close
    e.stopPropagation()
  }
  function onKeyDown (e: KeyDownEvent) {
    e.stopPropagation()
  }
</script>

<div
  class="modal-overlay"
  onclick={closeModal}
  onkeyup={onOverlayKeyUp}
  use:autofocus={{ refocusOnVisibilityChange: false }}
  role="button"
  tabindex="-1"
>
  <div
    class="modal-outer"
    class:size-auto={size === 'auto'}
    class:size-medium={size === 'medium'}
    class:size-large={size === 'large'}
    onclick={onModalClick}
    onkeydown={onKeyDown}
    role="button"
    tabindex="-1"
  >
    <div class="modal-inner">{@render children?.()}</div>
    <button
      class="close"
      title={I18n('close')}
      onclick={closeModal}
    >
      {@html icon('close')}
    </button>
  </div>
</div>

<style lang="scss">
  @import "general/scss/utils";
  .modal-overlay{
    background: rgb(black, 0.55);
    overflow: auto;
    @include position(fixed, 0, 0, 0, 0);
    // Above the dropdown, .leaflet-pane, .leaflet-control, .leaflet-bottom
    z-index: 1001;
    @include display-flex(row, baseline, center);
    overflow-y: auto;
  }
  .modal-outer{
    max-width: 100vw;
    position: relative;
    background-color: white;
    @include radius;
  }
  .size-auto{
    flex: 0 1 auto;
  }
  .size-medium{
    flex: 0 1 40rem;
  }
  .close{
    position: absolute;
    inset-block-start: 0.2em;
    inset-inline-end: 0;
    margin: 0;
    padding: 0;
    font-size: 1.8rem;
    @include text-hover($grey, $dark-grey);
    background-color: transparent;
  }
  .modal-inner{
    :global(h2){
      text-align: center;
      font-size: 1.2rem;
      @include sans-serif;
    }
  }
  .size-large{
    min-width: min(60em, 90vw);
  }
  /* Large screens */
  @media screen and (width >= $small-screen){
    .modal-outer{
      margin: 1em 0;
      padding: 1em;
    }
    .modal-inner{
      min-width: 10em;
      margin: 1em;
    }
  }
  /* Small screens */
  @media screen and (width < $small-screen){
    .modal-outer{
      margin: 0.5em 0;
      padding: 0.5em;
    }
  }
</style>
